import React, { useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Collapse, IconButton, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { StageDiffEntry } from "store/types";

const bandSx = {
  bgcolor: "#ede5f9",
  borderRadius: "4px",
  px: "6px",
  width: "100%"
} as const;

const titleSx = { fontSize: "12px", lineHeight: "16px", color: "#071f35", wordBreak: "break-word", flex: 1 } as const;
const dateSx = {
  fontSize: "12px",
  lineHeight: "16px",
  color: "#496682",
  whiteSpace: "nowrap",
  minWidth: "40px"
} as const;
const badgeSx = {
  alignSelf: "flex-start",
  fontSize: "11px",
  lineHeight: "16px",
  color: "#496682",
  bgcolor: "#dbe8f5",
  borderRadius: "20px",
  px: "8px"
} as const;

const formatDate = (date: string | null) => (date ? dayjs(date).format("MMM DD") : "");

/**
 * Deal stage-change timeline (`stage_diff`), rendered as a purple band on the card in Diff view (Figma
 * node 2701:67255 / 2701:67358). A single dated element shows a static row (title + date). When there is more
 * than one element the backend prepends a header element (`date: null`, e.g. "New > Invested"); the FE renders
 * that first element as the collapsed-band header and the rest as the expanded rows (a dateless element such as
 * "Left and returned" renders as a small badge with no date). The header text comes from the backend.
 */
export const StageDiffTimeline: React.FC<{ stageDiff?: StageDiffEntry[] }> = ({ stageDiff }) => {
  const [open, setOpen] = useState(false);

  if (!stageDiff?.length) return null;

  if (stageDiff.length === 1) {
    // Single dated element ("Added to {stage}" or one transition): title left, date right.
    const entry = stageDiff[0];
    return (
      <Stack direction="row" alignItems="center" gap="10px" sx={{ ...bandSx, minHeight: "28px" }}>
        <Typography sx={titleSx}>{entry.title}</Typography>
        <Typography sx={dateSx}>{formatDate(entry.date)}</Typography>
      </Stack>
    );
  }

  const header = stageDiff[0];
  const rows = stageDiff.slice(1);

  return (
    <Stack sx={{ ...bandSx, py: "4px" }}>
      {/* Collapsed header: backend header title on the left, expand caret on the right (no date). */}
      <Stack
        direction="row"
        alignItems="center"
        gap="10px"
        sx={{ cursor: "pointer", minHeight: "20px" }}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <Typography sx={titleSx}>{header.title}</Typography>
        <IconButton sx={{ p: 0, width: "16px", height: "16px" }}>
          <ArrowDropDownIcon
            sx={{
              fontSize: "16px",
              color: "#071f35",
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.15s linear"
            }}
          />
        </IconButton>
      </Stack>
      <Collapse in={open}>
        {/* Expanded body: dated rows show date (left) + title; a dateless element renders as a badge. */}
        <Stack gap="4px" sx={{ pt: "4px" }}>
          {rows.map((entry, index) =>
            entry.date ? (
              <Stack key={`${entry.title}-${index}`} direction="row" alignItems="center" gap="10px">
                <Typography sx={dateSx}>{formatDate(entry.date)}</Typography>
                <Typography sx={titleSx}>{entry.title}</Typography>
              </Stack>
            ) : (
              <Typography key={`${entry.title}-${index}`} sx={badgeSx}>
                {entry.title}
              </Typography>
            )
          )}
        </Stack>
      </Collapse>
    </Stack>
  );
};
